import {
  Alert,
  Autocomplete,
  CardContent,
  Chip,
  Grid,
  InputLabel,
  Select,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import React, { useEffect, useMemo, useState } from 'react';
import { Form } from 'react-bootstrap';
import {
  assignUserToProject,
  editUserRoleInProject,
} from '../../modules/project';
import { listAllUsers } from '../../modules/users';
import CustomButton from '../elements/CustomButton';
import Modal from '../elements/Modal';

const AssignRole = ({
  currentProject,
  open,
  setOpen,
  fetchUsersData,
  associatedUsers,
  mode,
  selectedUser,
  setMode,
}) => {
  const [error, setError] = useState(null);
  const [users, setUsers] = useState([]);
  const [role, setRole] = useState('member');
  const [selectedValues, setSelectedValues] = useState([]);
  const [inputValue, setInputValue] = useState('');
  const fetchUsers = async () => {
    const data = await listAllUsers(null, null, null, 'exclude-inactive');
    setUsers(data?.users);
  };

  const roleToUserRolesMap = {
    member: ['team-member'],
    admin: ['team-member'],
    'executive-manager': ['executive-manager'],
  };

  useEffect(() => {
    fetchUsers();
    if (mode === 'edit') {
      setRole(selectedUser?.role);
    }
  }, [open]);
  const handleClose = () => {
    setOpen(false);
    setError(null);
    setSelectedValues([]);
    setMode('add');
    setRole('member');
  };

  const handleEditSubmit = async (event) => {
    event.preventDefault();
    const payload = {
      user_id: selectedUser?.id,
      role,
    };
    try {
      await editUserRoleInProject(payload);
      handleClose();
      fetchUsersData();
    } catch (error) {
      setError(error.message || 'Failed to update role. Please try again.');
    }
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!selectedValues.length) {
      setError('Please select at least one user.');
      return;
    }
    const payload = selectedValues.map((user) => ({
      user_id: user?.id,
      role,
      project_id: currentProject.id,
    }));
    try {
      await assignUserToProject(payload);
      handleClose();
      fetchUsersData();
    } catch (error) {
      setError(error.message || 'Failed to update project. Please try again.');
    }
  };
  const finalUserList = useMemo(() => {
  const allowedRoles = roleToUserRolesMap[role] || [];
  return users?.filter(
    (user) =>
      allowedRoles.includes(user.role) &&
      !associatedUsers?.some(
        (associatedUser) => associatedUser?.user_id === user?.id
      )
  );
  }, [users, associatedUsers, role]);

  const handleRoleChange = (event) => {
    setRole(event.target.value);
    setSelectedValues([]);
  }

  return (
    <>
      <Modal
        open={open}
        modalTitle="Manage Team"
        handleClose={handleClose}
        width="sm">
        <CardContent sx={{ pt: 0 }}>
          <Form
            noValidate
            onSubmit={mode === 'edit' ? handleEditSubmit : handleSubmit}>
            <Grid container spacing={2}>
              {mode === 'edit' && (
                <Grid item xs={12} className="mb-2">
                  <InputLabel id="user">User</InputLabel>
                  <Typography variant="body1">{selectedUser?.name}</Typography>
                </Grid>
              )}
              <Grid item xs={12} className="mb-2">
                <Stack spacing={1}>
                  <InputLabel id="role" className="required">
                    Role
                  </InputLabel>
                  <Select
                    labelId="role"
                    id="role"
                    native
                    value={role}
                    sx={{
                      '& .MuiNativeSelect-select': {
                        padding: '14px',
                      },
                    }}
                    onChange={handleRoleChange}>
                    <option value={'member'}>Member</option>
                    <option value={'admin'}>Project Admin</option>
                    <option value={'executive-manager'}>
                      Executive Manager
                    </option>
                  </Select>
                </Stack>
              </Grid>
              {mode === 'add' && (
                <Grid item xs={12}>
                  <Stack spacing={1}>
                    <InputLabel id="users" className="required">
                      Users
                    </InputLabel>
                    <Autocomplete
                      multiple
                      value={selectedValues}
                      inputValue={inputValue}
                      onInputChange={(event, newInputValue) =>
                        setInputValue(newInputValue)
                      }
                      onChange={(event, newValue) =>
                        setSelectedValues(newValue)
                      }
                      renderTags={(value, getTagProps) =>
                        value.map((option, index) => (
                          <Chip
                            label={option.name}
                            {...getTagProps({ index })}
                            key={option.id}
                          />
                        ))
                      }
                      id="users"
                      options={finalUserList.map((user) => ({
                        name: `${user.first_name} ${user.last_name}`,
                        id: user?.id,
                        status: user?.status || '',
                      }))}
                      getOptionLabel={(option) => option.name}
                      renderOption={(props, option) => (
                        <li {...props} key={option.id}>
                          <div style={{ display: 'flex', alignItems: 'center' }}>
                            <span>{option.name}</span>
                            <span style={{ fontSize: '0.75rem', color: '#888', marginLeft: 6 }}>
                              ({option.status})
                            </span>
                          </div>
                        </li>
                      )}
                      isOptionEqualToValue={(option, value) =>
                        option.id === value.id
                      }
                      defaultValue={[]}
                      filterSelectedOptions
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          variant="outlined"
                          sx={{ border: '0px' }}
                          placeholder="Users"
                        />
                      )}
                    />
                  </Stack>
                </Grid>
              )}
            </Grid>
            {error && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {error}
              </Alert>
            )}
            <CustomButton buttonLabel={mode === 'edit' ? 'Save' : 'Assign'} />
          </Form>
        </CardContent>
      </Modal>
    </>
  );
};

export default AssignRole;
